import React, { useRef, useMemo, useState } from 'react';
import { push } from 'connected-react-router';
import { Form, Flex } from 'onefaceui-react-components';
import PropTypes from 'prop-types';

import { getResourceTo } from 'react/utils/utils';
import Loading from 'react/components/Loading';

class FormError extends Error {
  constructor(fields) {
    super("Form error");

    this.fields = fields;
  }
};

function BaseFormV2({
  id,
  children,
  entity,
  isLoading,
  defaultValues,
  saveAction,
  afterSubmit,
  requiredFields = [],
  dataTestId = 'base-form'
}) {
  const formRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isEdit = useMemo(() => !!id, [id]);

  const goToGridPage = () => {
    window.globalStore.dispatch(push(`/${entity}`));
  };

  const goToHelpPage = () => {
    const url = 'https://greenmile.help.descartesservices.com';

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleErrorFromResponse = ({ error }) => {
    let message = '';

    if (error && error.errorMessages) {
      const { i18nMessage, key } = error.errorMessages[0].resource;
      message = i18nMessage || key;
    } else {
      message = 'general.form.unknownError';
    }

    setErrorMessage(message);
  };

  const validateFields = (values) => {
    const errors = {};

    requiredFields.forEach((field) => {
      const fieldValue = values[field];
      const hasField = values.hasOwnProperty(field);
      const hasValue = fieldValue || typeof fieldValue === 'boolean' || (typeof fieldValue === 'number' && fieldValue === 0);

      if (hasField && !hasValue) {
        errors[field] = getResourceTo('default.fields.requiredField');
      }
    });

    if (Object.keys(errors).length) {
      throw new FormError(errors);
    }
  };

  const handleSubmit = async (values) => {
    formRef?.current?.clearErrors();
    setIsSaving(true);
    setErrorMessage('');

    try {
      validateFields(values);
      const response = await saveAction(values, isEdit);

      if (response?.error) {
        handleErrorFromResponse(response);
        return;
      }

      afterSubmit?.();
      goToGridPage();
    } catch (error) {
      if (error instanceof FormError) {
        formRef?.current?.setErrors(error.fields);
      } else {
        console.log(error);
      }

    } finally {
      setIsSaving(false);
    }
  };

  const renderError = () => {
    const className = errorMessage ? 'showError' : '';

    return (
      <div className={`formError ${className}`}>
        {getResourceTo(errorMessage)}
      </div>
    );
  };

  const onKeyPress = (event) => {
    const elementFocusedWhenPressed = event.target;

    if (event.key === 'Enter' && elementFocusedWhenPressed.tagName !== 'BUTTON') {
      event.preventDefault();
    }
  };


  return (
    <Form
      dataTestId={dataTestId}
      ref={formRef}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onKeyPress={onKeyPress}
      className={`${entity}Form general-form-v2`}
      noValidate
    >
      <Flex className="form-header" justifyContent="flex-end" alignItems="center">
        <Flex alignItems="center" gap={16}>
          <div
            className="helpButton"
            onClick={goToHelpPage}
            title={getResourceTo('Greenmile.Grid.Button.helpPage')}
          />
          {isSaving ? (
            <Loading width="20px" height="20px" />
          ) : (
            <button
              type="submit"
              className='saveButton'
              data-testid='save-button'
              disabled={isSaving}
              title={isEdit ? getResourceTo('default.button.edit') : getResourceTo('default.button.save')}
            />
          )}
          <div
            className="closeButton"
            onClick={goToGridPage}
            title={getResourceTo('default.button.cancel')}
            disabled={isSaving}
          />
        </Flex>
      </Flex>
      <Flex shouldShow={isLoading} justifyContent="center">
        <Loading width="74px" height="74px" />
      </Flex>
      <Flex shouldShow={!isLoading} flexDirection="column" className="form-body">
        {renderError()}
        {children}
      </Flex>
    </Form>
  );
}

BaseFormV2.propTypes = {
  children: PropTypes.node.isRequired,
  entity: PropTypes.string.isRequired,
  saveAction: PropTypes.func.isRequired,
  afterSubmit: PropTypes.func,
  id: PropTypes.string
};

BaseFormV2.defaultProps = {
  id: null
};

export default BaseFormV2;
